import React, { Suspense, useRef } from 'react';
import { Table } from 'reactstrap';
import { Row, Col } from 'src/componentes/grid';

import LoadingScreen from 'src/componentes/LoadingScreen';
import guid from 'src/utils/guid';
import DropDown from 'src/paginas/item/dropdown';
import PolaridadeGrafico from './polaridade-grafico';

const Grafico = React.lazy(() => import('src/componentes/chart'));

const TAMANHO_MINIMO_CLIQUE = 20;
const INTERVALO_CLIQUE_DUPLICADO = 500;
const ATRIBUTO_MONITORADO = 'data-clique-plot-monitorado';

function GraficoItem({
  grafico,
  item,
  height,
  filtro,
  dataClick,
  handleCloseModal,
  isConsultaExterna,
  graficoColunasEmpilhadas,
  transparent,
  showTable = false,
  showChart = true,
  graficoPizza = false,
  tituloPersonalizado = null,
}) {
  const ultimoPlotClicado = useRef(null);
  const aoClicarNoSvgRef = useRef(null);

  const renderColunas = () => {
    const categories = grafico.tabelaGrafico.colunas;

    return (
      categories &&
      categories.map((category, index) => <th key={index}>{category}</th>)
    );
  };

  const renderLabelItem = (valor) => <span>{valor}</span>;

  const renderDados = () => {
    const itens = grafico.tabelaGrafico.dados;

    return itens.map((linha, indexLinha) => (
      <tr key={indexLinha}>
        {linha.item.map((x, indexColuna) => (
          <td key={indexColuna}>
            {x.item ? (
              <a tabIndex="-1">
                <DropDown
                  showMenu={!isConsultaExterna}
                  handleCloseModal={handleCloseModal}
                  filtro={filtro}
                  item={x.item}
                  renderLabel={() => renderLabelItem(x.valor)}
                />
              </a>
            ) : (
              x.valor
            )}
          </td>
        ))}
      </tr>
    ));
  };

  const dataPlot = (ev, dados) => {
    const dataSet = grafico?.dataSet || [];
    const indiceDoDataset = ev?.data?.resolvidoPorPosicao
      ? dados.datasetIndex
      : (graficoColunasEmpilhadas && ev?.data?.datasetIndex) || 0;
    const dataset = dataSet[indiceDoDataset] || null;

    if (!dataset || !dataClick) return;

    const dado = dataset.data[dados.index];

    if (graficoColunasEmpilhadas)
      dataClick(dado, ev?.data?.categoryLabel, ev?.data?.datasetName);
    else dataClick(dado, ev);
  };

  const aoClicarNoPlot = (ev, dados) => {
    const chave = `${dados?.datasetIndex ?? 0}-${dados?.index}`;
    const agora = Date.now();
    const ultimo = ultimoPlotClicado.current;

    ultimoPlotClicado.current = { chave, tempo: agora };

    if (
      ultimo &&
      ultimo.chave === chave &&
      agora - ultimo.tempo < INTERVALO_CLIQUE_DUPLICADO
    )
      return;

    dataPlot(ev, dados);
  };

  const temValor = (dado) => {
    const valor = parseFloat(dado?.value);

    return isFinite(valor) && valor !== 0;
  };

  const lerGeometria = (plot) => {
    const caixa = plot.getBoundingClientRect();

    return {
      x: caixa.left,
      y: caixa.top,
      largura: caixa.width,
      altura: caixa.height,
    };
  };

  const lerCaixaDoRotulo = (rotulo) => {
    try {
      const caixa = rotulo?.getBoundingClientRect();

      return caixa?.width
        ? {
            x: caixa.left,
            y: caixa.top,
            width: caixa.width,
            height: caixa.height,
          }
        : null;
    } catch {
      return null;
    }
  };

  const obterGruposDePlots = (svg) =>
    Array.from(
      svg.querySelectorAll('g[class*="-plots"] g[class*="plot-group"]')
    )
      .filter(
        (grupo) =>
          !(grupo.getAttribute('class') || '').includes('error-plot-group')
      )
      .map((grupo) =>
        Array.from(grupo.children).filter((no) => no.tagName === 'rect')
      );

  const obterGruposDeRotulos = (svg) =>
    Array.from(
      svg.querySelectorAll('g[class*="label-group"][class*="datalabels"]')
    ).map((grupo) =>
      Array.from(grupo.children).filter((no) => no.tagName === 'text')
    );

  const obterColunas = (svg) => {
    const gruposDePlots = obterGruposDePlots(svg);
    const gruposDeRotulos = obterGruposDeRotulos(svg);
    const dataSet = grafico?.dataSet || [];
    const categorias = grafico?.categories?.[0]?.category || [];

    if (!dataSet.length || gruposDePlots.length !== dataSet.length) return null;

    const segmentos = [];

    gruposDePlots.forEach((plots, datasetIndex) => {
      const rotulos = gruposDeRotulos[datasetIndex];
      const rotulosDoDataset =
        rotulos?.length === plots.length ? rotulos : null;

      plots.forEach((plot, posicao) => {
        const geometria = lerGeometria(plot);

        if (!Object.values(geometria).every((valor) => isFinite(valor))) return;

        segmentos.push({
          ...geometria,
          rotulo:
            rotulosDoDataset && lerCaixaDoRotulo(rotulosDoDataset[posicao]),
          datasetIndex,
        });
      });
    });

    const colunas = [];

    segmentos
      .sort((a, b) => a.x - b.x)
      .forEach((segmento) => {
        const atual = colunas[colunas.length - 1];
        const tolerancia = Math.max(2, segmento.largura / 2);

        if (atual && Math.abs(segmento.x - atual[0].x) <= tolerancia)
          atual.push(segmento);
        else colunas.push([segmento]);
      });

    if (!colunas.length || colunas.length !== categorias.length) return null;

    colunas.forEach((segmentos, index) =>
      segmentos.forEach((segmento) => {
        segmento.index = index;
      })
    );

    const comValor = colunas
      .map((segmentos) =>
        segmentos.filter((segmento) =>
          temValor(dataSet[segmento.datasetIndex]?.data?.[segmento.index])
        )
      )
      .filter((segmentos) => segmentos.length);

    return comValor.length ? comValor : null;
  };

  const localizarColuna = (colunas, ponto) => {
    let maisProxima = null;

    colunas.forEach((segmentos) => {
      const { x, largura } = segmentos[0];
      const distancia = Math.abs(ponto.x - (x + largura / 2));

      if (distancia > Math.max(largura, TAMANHO_MINIMO_CLIQUE) / 2) return;

      if (!maisProxima || distancia < maisProxima.distancia)
        maisProxima = { distancia, segmentos };
    });

    return maisProxima?.segmentos || null;
  };

  const dentroDoRotulo = (rotulo, ponto) =>
    !!rotulo &&
    ponto.x >= rotulo.x &&
    ponto.x <= rotulo.x + rotulo.width &&
    ponto.y >= rotulo.y &&
    ponto.y <= rotulo.y + rotulo.height;

  const localizarPeloRotulo = (segmentos, ponto) => {
    let maisProximo = null;

    segmentos.forEach((segmento) => {
      if (!dentroDoRotulo(segmento.rotulo, ponto)) return;

      const distancia = Math.abs(
        ponto.y - (segmento.rotulo.y + segmento.rotulo.height / 2)
      );

      if (!maisProximo || distancia < maisProximo.distancia)
        maisProximo = { distancia, segmento };
    });

    return maisProximo?.segmento || null;
  };

  const localizarPeloPlot = (segmentos, ponto) =>
    segmentos.find(({ y, altura }) => ponto.y >= y && ponto.y <= y + altura) ||
    null;

  const localizarPelaFaixaMinima = (segmentos, ponto) => {
    if (segmentos.length !== 1) return null;

    const [segmento] = segmentos;
    const altura = Math.max(segmento.altura, TAMANHO_MINIMO_CLIQUE);
    const topo = segmento.y + segmento.altura / 2 - altura / 2;

    return ponto.y >= topo && ponto.y <= topo + altura ? segmento : null;
  };

  const localizarSegmentoClicado = (svg, ponto) => {
    const colunas = obterColunas(svg);

    if (!colunas) return null;

    const segmentos = localizarColuna(colunas, ponto);

    if (!segmentos) return null;

    return (
      localizarPeloRotulo(segmentos, ponto) ||
      localizarPeloPlot(segmentos, ponto) ||
      localizarPelaFaixaMinima(segmentos, ponto)
    );
  };

  const aoClicarNoSvg = (svg, evento) => {
    const segmento = localizarSegmentoClicado(svg, {
      x: evento.clientX,
      y: evento.clientY,
    });

    if (!segmento) return;

    evento.stopPropagation();

    const dados = {
      index: segmento.index,
      datasetIndex: segmento.datasetIndex,
      categoryLabel:
        grafico?.categories?.[0]?.category?.[segmento.index]?.label,
      datasetName: grafico?.dataSet?.[segmento.datasetIndex]?.seriesname,
      resolvidoPorPosicao: true,
    };

    aoClicarNoPlot(
      {
        data: dados,
        preventDefault: () => evento.preventDefault(),
        stopPropagation: () => evento.stopPropagation(),
      },
      dados
    );
  };

  aoClicarNoSvgRef.current = aoClicarNoSvg;

  const prepararCliqueNosPlots = (chart) => {
    if (!dataClick) return;

    const svg = chart?.options?.containerElement?.querySelector('svg');

    if (!svg) return;

    if (obterColunas(svg))
      obterGruposDePlots(svg).forEach((plots) =>
        plots.forEach((plot) => {
          plot.style.cursor = 'pointer';
        })
      );

    if (svg.hasAttribute(ATRIBUTO_MONITORADO)) return;

    svg.setAttribute(ATRIBUTO_MONITORADO, '1');
    svg.addEventListener(
      'click',
      (evento) => aoClicarNoSvgRef.current(svg, evento),
      true
    );
  };

  const montarEventos = () => ({
    dataPlotClick: aoClicarNoPlot,
    renderComplete: (ev) => prepararCliqueNosPlots(ev?.sender),
  });

  const renderTituloPersonalizado = () => {
    const {
      titulo,
      unidadeMedidaPersonalizada,
      background,
      marginBottom,
      polaridade,
    } = tituloPersonalizado;

    return (
      <div
        style={{
          fontSize: 14,
          overflow: 'hidden',
          width: '100%',
          minHeight: 30,
          backgroundColor: background ? background : 'transparent',
          padding: '5px 10px',
          textAlign: 'left',
          marginBottom: marginBottom != null ? marginBottom : 15,
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <div
            style={{
              overflow: 'hidden',
              whiteSpace: 'nowrap',
              textOverflow: 'ellipsis',
              maxWidth: '30vw',
            }}
          >
            {titulo}
          </div>

          <PolaridadeGrafico polaridade={polaridade} item={item} />
        </div>
        {tituloPersonalizado.unidadeMedida ? (
          <span style={{ fontSize: 12 }}>
            {item &&
              item.unidadeMedida &&
              item.tipo &&
              item.tipo.id != 4 &&
              item.unidadeMedida.descricao}
          </span>
        ) : (
          unidadeMedidaPersonalizada != null && (
            <span style={{ fontSize: 12 }}>{unidadeMedidaPersonalizada}</span>
          )
        )}
      </div>
    );
  };

  const dadosGrafico =
    grafico && grafico.dataSet && grafico.dataSet.length > 0 ? true : false;
  const tipoDoGrafico =
    grafico && grafico.tipoGrafico != null ? grafico.tipoGrafico : 'MSCombi2D';
  let config;

  if (tipoDoGrafico == 'Bubble' || tipoDoGrafico == 'Scatter') {
    config = {
      width: '100%',
      height: showTable
        ? height
          ? height / 2
          : '400'
        : height
        ? height - 10
        : '400',
      type: tipoDoGrafico,
      containerBackgroundOpacity: grafico && grafico.containerBackgroundOpacity,
      dataFormat: item ? null : 'json',
      dataSource: {
        chart: {
          ...(grafico && grafico.chart),
          caption:
            tituloPersonalizado == null
              ? item
                ? item && item.nome
                : ' '
              : ' ',
        },
        annotations:
          grafico && grafico.annotations ? grafico.annotations : null,
        categories:
          grafico && grafico.usarCategorias
            ? grafico.categories
            : [
                {
                  verticalLineDashed: '1',
                  verticalLineDashLen: '1',
                  verticalLineDashGap: '1',
                  verticalLineThickness: '1',
                  verticalLineColor: '#000000',
                  category: grafico.dataSet && grafico.dataSet[0].data,
                },
              ],
        dataSet: dadosGrafico ? grafico.dataSet : null,
      },
      events: montarEventos(),
    };
  }

  if (
    tipoDoGrafico == 'msbar2d' ||
    tipoDoGrafico == 'Bar2D' ||
    tipoDoGrafico == 'MSCombi2D' ||
    tipoDoGrafico == 'StackedColumn2d' ||
    tipoDoGrafico == 'mscombidy2d' ||
    tipoDoGrafico == 'stackedbar2d' ||
    tipoDoGrafico == 'mscolumn2d' ||
    tipoDoGrafico == 'doughnut2d'
  )
    config = {
      width: '100%',
      height: showTable
        ? height
          ? height / 2
          : '400'
        : height
        ? height - 10
        : '400',
      type: tipoDoGrafico,
      containerBackgroundOpacity: grafico && grafico.containerBackgroundOpacity,
      dataFormat: item ? null : 'json',
      dataSource: {
        chart: {
          ...(grafico && grafico.chart),
          caption:
            tituloPersonalizado == null
              ? item
                ? item && item.nome
                : ' '
              : ' ',
          chartRightMargin: 60,
        },
        annotations:
          grafico && grafico.annotations ? grafico.annotations : null,
        categories: grafico && grafico.categories,
        data: dadosGrafico ? grafico.dataSet && grafico.dataSet[0].data : null,
        dataset: dadosGrafico ? grafico.dataSet : null,
      },
      events: montarEventos(),
    };

  if (graficoPizza || tipoDoGrafico == 'Pie2D')
    config = {
      width: '100%',
      height: height ? height - 50 : '400',
      type:
        grafico && grafico.tipoGrafico != null
          ? grafico.tipoGrafico
          : 'mscombi2d',
      containerBackgroundOpacity: grafico && grafico.containerBackgroundOpacity,
      dataFormat: item ? null : 'json',
      dataSource: {
        chart: {
          ...(grafico && grafico.chart),
          captionPadding: 0,
          chartLeftMargin: 0,
          chartRightMargin: 0,
          chartTopMargin: 0,
          chartBottomMargin: 0,
          legendNumColumns: 2,
          legendItemFontSize: 11,
          caption:
            tituloPersonalizado == null
              ? item
                ? item && item.nome
                : ' '
              : ' ',
          bgColor: transparent ? '#FFFFFF00' : grafico?.chart.bgColor, // Setting the background color to transparent
          bgAlpha: transparent ? '0' : grafico?.chart.bgAlpha,
          //"subcaption": grafico && grafico.tipoTabela === "Desvio" ? grafico && grafico.chart && grafico.chart.subcaption : null,
        },
        annotations:
          grafico && grafico.annotations ? grafico.annotations : null,
        categories: grafico && grafico.categories,
        data: dadosGrafico
          ? grafico.dataSet[0] && grafico.dataSet[0].data
          : null,
      },
    };

  if (tipoDoGrafico == 'Pareto2D')
    config = {
      width: '100%',
      height: showTable
        ? height
          ? height / 2
          : '400'
        : height
        ? height - 10
        : '400',
      type: tipoDoGrafico,
      containerBackgroundOpacity: grafico && grafico.containerBackgroundOpacity,
      dataFormat: item ? null : 'json',
      dataSource: {
        chart: {
          ...(grafico && grafico.chart),
          caption:
            tituloPersonalizado == null
              ? item
                ? item && item.nome
                : ' '
              : ' ',
        },
        annotations:
          grafico && grafico.annotations ? grafico.annotations : null,
        categories: grafico && grafico.categories,
        data: dadosGrafico ? grafico.dataSet && grafico.dataSet[0].data : null,
        dataset: dadosGrafico ? grafico.dataSet : null,
      },
    };

  return (
    <Suspense fallback={<LoadingScreen />}>
      {showChart && (
        <Row id={`relatorioPag${!item ? '' : item && item.id}`}>
          <Col id="graficoValoresItem" style={{ borderSpacing: 0, padding: 2 }}>
            {tituloPersonalizado && renderTituloPersonalizado()}
            <Grafico key={guid()} {...config} />
          </Col>
        </Row>
      )}
      <div>
        <Row>
          <Col md={12}>
            {showTable && grafico && grafico.tabelaGrafico.dados && (
              <Table
                hover
                size="sm"
                style={{
                  background: '#FFF',
                  marginTop: 15,
                  overflow: 'visible',
                }}
              >
                <thead>
                  <tr>{renderColunas()}</tr>
                </thead>
                <tbody>{renderDados()}</tbody>
              </Table>
            )}
          </Col>
        </Row>
      </div>
    </Suspense>
  );
}

function propsGraficoIguais(prev, next) {
  return (
    prev.grafico === next.grafico &&
    prev.height === next.height &&
    prev.item === next.item &&
    prev.filtro === next.filtro &&
    prev.dataClick === next.dataClick &&
    prev.handleCloseModal === next.handleCloseModal &&
    prev.isConsultaExterna === next.isConsultaExterna &&
    prev.graficoColunasEmpilhadas === next.graficoColunasEmpilhadas &&
    prev.transparent === next.transparent &&
    prev.showTable === next.showTable &&
    prev.showChart === next.showChart &&
    prev.graficoPizza === next.graficoPizza &&
    prev.tituloPersonalizado === next.tituloPersonalizado
  );
}

export default React.memo(GraficoItem, propsGraficoIguais);
